/**
 * PlaceCandidateModal — opened from the Awaiting placement pool's
 * "+ Assign to class" button. Picks a class and enrolls the
 * candidate. The classes router auto-promotes status='accepted' →
 * 'active' on the same call, so this is a single round-trip handoff
 * to the enrolled roster.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useClasses,
  useEnrollStudents,
  type Candidate,
} from "../../api/school";

interface Props {
  candidate: Candidate;
  schoolId: string;
  onClose: () => void;
}

export function PlaceCandidateModal({ candidate, onClose }: Props) {
  const { t } = useTranslation();
  const classes = useClasses();
  const [classId, setClassId] = useState("");
  const [err, setErr] = useState<string | null>(null);

  // Default to the first class matching the candidate's grade so the
  // common case is one click — the user can submit without ever
  // touching the picker.
  const sameGrade =
    classes.data?.find((c) => c.grade === candidate.grade) ?? classes.data?.[0];
  const effectiveClassId = classId || sameGrade?.id || "";
  const enroll = useEnrollStudents(effectiveClassId || undefined);

  async function submit() {
    if (!effectiveClassId) {
      setErr(t("admissions.place.errClassRequired"));
      return;
    }
    setErr(null);
    try {
      await enroll.mutateAsync([candidate.id]);
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-slate-900/40 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-800">
            {t("admissions.place.title", { name: candidate.name_zh })}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 text-xl leading-none"
          >
            ×
          </button>
        </header>

        <div className="p-6 space-y-3">
          <p className="text-xs text-slate-500">
            {t("admissions.place.description")}
          </p>
          <label className="block text-sm">
            <span className="block text-xs text-slate-600 mb-1">{t("admissions.place.classLabel")}</span>
            <select
              value={effectiveClassId}
              onChange={(e) => setClassId(e.target.value)}
              className="w-full px-2 py-1.5 rounded border border-slate-200"
            >
              {classes.data?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} · {c.grade}
                  {c.section ?? ""}
                </option>
              ))}
            </select>
          </label>

          {err && <div className="text-xs text-red-600">{err}</div>}
        </div>

        <footer className="px-6 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={enroll.isPending}
            className="px-3 py-1.5 text-sm text-slate-600 rounded hover:bg-slate-100 disabled:opacity-50"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={enroll.isPending}
            className="px-4 py-1.5 text-sm font-medium rounded bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50"
          >
            {enroll.isPending ? t("admissions.place.submitting") : t("admissions.place.submit")}
          </button>
        </footer>
      </div>
    </div>
  );
}
